import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

type ImageType = 'original' | 'sam2' | 'siglip'

interface PhotoReelDropdownProps {
  currentImageType: ImageType
  availableImageTypes: ImageType[]
  onImageTypeChange: (type: ImageType) => void
  disabled?: boolean
}

const DropdownContainer = styled.div`
  position: relative;
`

const DropdownTrigger = styled.button`
  all: unset;
  background: rgba(25, 25, 25, 0.98) !important;
  color: white !important;
  border: 2px solid rgba(255, 255, 255, 0.5) !important;
  border-radius: 4px !important;
  padding: 1.25rem 2rem;
  font-size: 0.95rem;
  cursor: pointer;
  display: inline-flex !important;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  font-weight: 600;
  white-space: nowrap;
  min-height: 48px;
  min-width: 160px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  box-sizing: border-box;

  &:hover {
    background: rgba(35, 35, 35, 0.98) !important;
    border-color: rgba(255, 255, 255, 0.7) !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const DropdownContent = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  background: rgba(15, 15, 15, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  min-width: 160px;
  padding: 0.25rem;
  margin-top: 0.5rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6);
  z-index: 1000;
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
`

const DropdownItem = styled.div<{ $isActive: boolean }>`
  padding: 0.75rem 1rem;
  background: ${({ $isActive }) =>
    $isActive ? 'rgba(70, 130, 180, 0.3)' : 'transparent'};
  color: ${({ $isActive }) => ($isActive ? '#87CEEB' : 'white')};
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.875rem;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`

const ChevronIcon = styled.div<{ $isOpen: boolean }>`
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid white;
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s ease;
  flex-shrink: 0;
  margin-left: auto;
  align-self: center;
`

const DropdownText = styled.span`
  flex: 1;
  text-align: left;
`

const getDisplayName = (type: ImageType): string => {
  switch (type) {
    case 'sam2':
      return 'SAM2 Processed'
    case 'siglip':
      return 'SIGLIP Processed'
    default:
      return 'Original'
  }
}

export const PhotoReelDropdown: React.FC<PhotoReelDropdownProps> = ({
  currentImageType,
  availableImageTypes,
  onImageTypeChange,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleItemClick = (type: ImageType) => {
    onImageTypeChange(type)
    setIsOpen(false)
  }

  return (
    <DropdownContainer ref={dropdownRef}>
      <DropdownTrigger disabled={disabled} onClick={() => setIsOpen(!isOpen)}>
        <DropdownText>{getDisplayName(currentImageType)}</DropdownText>
        <ChevronIcon $isOpen={isOpen} />
      </DropdownTrigger>
      <DropdownContent $isOpen={isOpen}>
        {availableImageTypes.map((type) => (
          <DropdownItem
            key={type}
            $isActive={currentImageType === type}
            onClick={() => handleItemClick(type)}>
            {getDisplayName(type)}
          </DropdownItem>
        ))}
      </DropdownContent>
    </DropdownContainer>
  )
}
